import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Page, Button, alert } from 'equasion-lib';
import {
  FiAlertTriangle,
  FiBox,
  FiCheck,
  FiEdit2,
  FiPrinter,
  FiShoppingBag,
  FiXCircle,
  FiLoader
} from 'react-icons/fi';
import { salesOrdersTabPath } from '@/lib/pickVerifyShip';
import ReprintStickersButton from '@/lib/shared-coms/ReprintStickersButton';
import CertificateOfOriginButton from '@/lib/shared-coms/CertificateOfOriginButton';
import { documentActionLabel, type PrintableOrderDocument } from '@/lib/rxPrint';
import { SLOW_SHIP_SECONDS, useElapsedSeconds, type ShipPhase, type ShipRun } from '@/lib/shipRun';
import { formatSize, packageTypeLabel, shipPackagesPath } from '@/lib/shipPackages';
import { HOME_PATH, SHIPPED_NOTICE_SECONDS, shippedNotice } from '@/lib/shipHandoff';
import './SalesOrderDocuments.scss';

// Warehouse is on react-icons 4.x; RiArchiveStackLine only shipped in 5.x. Same Remix path.
const RiArchiveStackLine = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M4 5H20V3H4V5ZM20 9H4V7H20V9ZM3 11H10V13H14V11H21V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V11ZM16 13V15H8V13H5V19H19V13H16Z" />
  </svg>
);

const LoadingIcon = () => <FiLoader className="doc-button-spinner" />;

const downloadFile = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback to window.open if fetch fails (e.g. CORS)
    window.open(url, '_blank');
  }
};

const ProgressStep = ({ done, label, detail }: { done: boolean; label: string; detail: string }) => (
  <li className={['ship-step', done ? 'is-done' : 'is-waiting'].join(' ')}>
    {done ? <FiCheck aria-hidden="true" /> : <FiLoader className="doc-button-spinner" aria-hidden="true" />}
    <span className="ship-step-label">{label}</span>
    <span className="ship-step-detail">{detail}</span>
  </li>
);

/**
 * The top of the page while a ship is under way — the replacement for the old full-screen
 * "Processing…" spinner. The operator lands here the moment they tap Generate Labels and watches
 * each piece arrive: the carrier label (bought first, before the ship commits), the shipment
 * itself, then the generated documents.
 */
const ShipProgress = ({
  phase,
  run,
  orderLabel,
  expectsCarrierLabel,
  labelReady,
  documentsReady,
  documentsExpected,
  onBackToShipping,
  checkTimedOut = false,
  onCheckAgain = undefined as undefined | (() => void)
}: {
  phase: ShipPhase;
  run?: ShipRun;
  orderLabel: string;
  expectsCarrierLabel: boolean;
  labelReady: boolean;
  documentsReady: number;
  documentsExpected: number;
  onBackToShipping: () => void;
  checkTimedOut?: boolean;
  onCheckAgain?: () => void;
}) => {
  const elapsed = useElapsedSeconds(run?.startedAt, phase === 'running');

  if (phase === 'failed') {
    return (
      <div className="ship-progress is-failed" role="alert">
        <div className="ship-progress-head">
          <FiAlertTriangle aria-hidden="true" />
          <h3>Could not ship {run?.label || orderLabel}</h3>
        </div>
        <p className="ship-progress-error">{run?.error || 'The shipment was not recorded.'}</p>
        <p className="ship-progress-note">
          {labelReady
            ? 'Nothing was shipped. The label already bought is kept, so trying again will not buy another — it can be printed once the order ships.'
            : 'Nothing was shipped.'}
        </p>
        <Button type="primary" fullWidth onClick={onBackToShipping}>
          Back to Shipping
        </Button>
      </div>
    );
  }

  if (phase === 'pending') {
    // The request's answer never arrived (dropped wifi, a gateway timeout): the server may have
    // shipped it anyway, so say so instead of "nothing was shipped", and keep checking the order.
    const lostAnswer = run?.status === 'failed' && run.outcomeUnknown;
    return (
      <div className="ship-progress is-pending" role="status">
        <div className="ship-progress-head">
          {checkTimedOut ? (
            <FiAlertTriangle aria-hidden="true" />
          ) : (
            <FiLoader className="doc-button-spinner" aria-hidden="true" />
          )}
          <h3>
            {lostAnswer
              ? `Lost the connection while shipping ${run.label}`
              : `${orderLabel} has not finished shipping`}
          </h3>
        </div>
        <p className="ship-progress-note">
          {checkTimedOut
            ? 'It still has not shipped. If nobody else is shipping it, go back to Shipping and try again.'
            : lostAnswer
            ? 'Checking whether it went through — this page fills in if it did.'
            : 'If it was just started, this page fills in when it finishes.'}
        </p>
        {checkTimedOut && onCheckAgain && (
          <Button type="secondary" fullWidth onClick={onCheckAgain}>
            Check Again
          </Button>
        )}
        <Button type="secondary" fullWidth onClick={onBackToShipping}>
          Go to Shipping
        </Button>
      </div>
    );
  }

  const shipped = phase === 'shipped';
  return (
    <div className={['ship-progress', shipped ? 'is-shipped' : 'is-running'].join(' ')} role="status">
      <ol className="ship-steps">
        {expectsCarrierLabel && (
          <ProgressStep
            done={labelReady}
            label="Shipping label"
            detail={
              !labelReady ? 'Buying from carrier…' : shipped ? 'Ready' : 'Bought — prints once shipped'
            }
          />
        )}
        <ProgressStep
          done={shipped}
          label={run?.kind === 'bundle' ? 'Bundle shipped' : 'Order shipped'}
          detail={shipped ? 'Done' : 'Recording shipment…'}
        />
        <ProgressStep
          done={documentsExpected > 0 && documentsReady >= documentsExpected}
          label="Documents"
          detail={`${Math.min(documentsReady, documentsExpected)} of ${documentsExpected} ready`}
        />
      </ol>
      {!shipped && elapsed >= SLOW_SHIP_SECONDS && (
        <p className="ship-progress-note">
          Still working ({elapsed}s) — the carrier is slow to answer. This keeps going even if you
          leave this page.
        </p>
      )}
    </div>
  );
};

const SalesOrderDocuments = ({
  salesOrder,
  isPolling,
  generationTimedOut,
  onRetryPolling,
  onStickersReprinted = undefined as undefined | (() => void),
  hasScheduledItems,
  // False for FOB and freight ships, which never produce a carrier label document.
  expectsCarrierLabel = true,
  // True for a shipment to Puerto Rico, which files a Certificate of Origin at ship.
  expectsCertificateOfOrigin = false,
  onCertificateOfOriginGenerated = undefined as undefined | (() => void),
  // RxPrint on: every button sends its document to the mapped printer (see @/lib/rxPrint).
  rxPrintEnabled = false,
  sendingKey = null as string | null,
  onSendToPrinter = undefined as
    | undefined
    | ((key: string, documents: PrintableOrderDocument[]) => void),
  // Where the ship stands (see @/lib/shipRun). Defaults to shipped: a page rendered without it
  // behaves exactly as before.
  shipPhase = 'shipped' as ShipPhase,
  shipRun = undefined as ShipRun | undefined,
  pendingCheckTimedOut = false
}) => {
  const [searchParams] = useSearchParams();
  const isShipping = searchParams.get('shipping');
  const navigate = useNavigate();
  const {
    id: orderId,
    vendorSalesOrderNumber,
    vendorStatus,
    orderItems,
    packingListDocument,
    ssccLabelDocument,
    shippingLabelDocuments,
    t3ReportDocument,
    t4ReportDocument,
    invoiceDocument,
    stickersDocument,
    certificateOfOriginDocument,
    shipsToPuertoRico,
    shippingBundle,
    shipPackages,
    shipPackagesEditable
  } = salesOrder || {};
  // BUNDLED SHIPPING. The members that went out in the same carton. Each member's documents live
  // on that member (own packing list, own invoice, own SSCC/T4/EPCIS, and its own COPY of the
  // shared label), so this strip is pure navigation — tap a sibling to print its paperwork. Shown
  // only when there is more than one member; a one-order bundle is just a shipped order.
  const bundleMembers = shippingBundle?.orders?.length > 1 ? shippingBundle.orders : [];
  const isBundled = bundleMembers.length > 0;
  const bundleLabel = shippingBundle?.bundleNumber;

  const packingListPdf = packingListDocument?.link;
  const invoicePdf = invoiceDocument?.link;
  const stickersZpl = stickersDocument?.link;
  const certificateOfOriginPdf = certificateOfOriginDocument?.link;
  const ssccLabelPdf = ssccLabelDocument?.link;
  const t3ReportPdf = t3ReportDocument?.link;
  const t4ReportPdf = t4ReportDocument?.link;
  const labelReady = shippingLabelDocuments?.length > 0;

  const totalQuantity = orderItems?.reduce((a, { quantity }) => a + quantity, 0) ?? 0;
  const orderLabel = `SO #${vendorSalesOrderNumber || ''}`;
  const shipped = shipPhase === 'shipped';
  // The progress panel shows from the tap of Generate Labels until the shipment is recorded, and
  // stays (all ticked) for the rest of that visit so the operator sees it complete.
  const showProgress = !shipped || !!shipRun;

  // The same set the data layer waits on before it calls the documents ready.
  const expectedDocuments = [
    packingListPdf,
    invoicePdf,
    ...(expectsCertificateOfOrigin ? [certificateOfOriginPdf] : []),
    ...(expectsCarrierLabel ? [labelReady] : []),
    ...(hasScheduledItems ? [ssccLabelPdf, t4ReportPdf] : [])
  ];
  const documentsReady = expectedDocuments.filter(Boolean).length;

  const returnToShipping = () => navigate(salesOrdersTabPath('shipping'));
  const backToShippingPage = () => navigate(`/sales-order-shipping/${orderId}`);
  // Done: straight to the home screen for the next picking slip — every order, either workflow.
  // The confirmation is for a ship this visit watched go through; a reprint visit (a scanned
  // slip, the home screen's Last shipped bar) just goes home.
  const finish = () => {
    if (shipRun?.status === 'succeeded') {
      const notice = shippedNotice(shipRun, false);
      alert(notice.title, notice.message, SHIPPED_NOTICE_SECONDS, 'success');
    }
    navigate(HOME_PATH);
  };

  // One button's tap: to the RxPrint printers when RxPrint is on (anything not sent is downloaded),
  // otherwise a download — and the label says which.
  const printDocument = (key: string, doc, filename: string) => {
    if (!doc?.link) return;
    const download = () => downloadFile(doc.link, filename);
    if (rxPrintEnabled && onSendToPrinter) onSendToPrinter(key, [{ id: doc.id, download }]);
    else download();
  };
  const actionLabel = (key: string, name: string) =>
    sendingKey === key ? 'Sending…' : documentActionLabel(rxPrintEnabled, name);
  const sending = !!sendingKey;
  // A document still on its way: a spinner while the page is still waiting for it (or for the ship
  // that will produce it), else the plain printer icon on a disabled button.
  const waiting = isPolling || shipPhase === 'running' || shipPhase === 'pending';
  const docIcon = (ready: unknown) => (!ready && waiting ? <LoadingIcon /> : <FiPrinter />);
  // Freshly arrived documents announce themselves while the page is filling in.
  const docClass = (ready: unknown) =>
    ['document-print-button', ready && showProgress && 'is-ready'].filter(Boolean).join(' ');

  const shippingLabelButtons =
    !shippingLabelDocuments || shippingLabelDocuments.length === 0 ? (
      expectsCarrierLabel ? (
        <Button
          type="secondary"
          className="document-print-button"
          fullWidth
          disabled
          iconBefore={docIcon(false)}
        >
          {documentActionLabel(rxPrintEnabled, 'Shipping Label')}
        </Button>
      ) : null
    ) : (
      shippingLabelDocuments.map((doc, index) => (
        <Button
          key={doc.id}
          type="secondary"
          className={docClass(shipped)}
          fullWidth
          // The label is filed before the ship commits, but it is not printable until the order
          // has actually shipped: the ship can still fail its own checks, and a label on a carton
          // that was never recorded as shipped would leave with no inventory movement or pedigree.
          disabled={sending || !shipped}
          iconBefore={docIcon(shipped)}
          onClick={() =>
            printDocument(
              `shipping-label-${doc.id}`,
              doc,
              doc.filename || `shipping-label-${index + 1}.zpl`
            )
          }
        >
          {actionLabel(
            `shipping-label-${doc.id}`,
            shippingLabelDocuments.length === 1 ? 'Shipping Label' : `Shipping Label ${index + 1}`
          )}
        </Button>
      ))
    );
  const ssccButton = hasScheduledItems && (
    <Button
      type="secondary"
      className={docClass(ssccLabelPdf)}
      fullWidth
      disabled={!ssccLabelPdf || sending}
      iconBefore={docIcon(ssccLabelPdf)}
      onClick={() => printDocument('sscc', ssccLabelDocument, 'sscc-label.pdf')}
    >
      {actionLabel('sscc', 'SSCC Label')}
    </Button>
  );
  // What goes on the carton before it leaves the bench: the carrier label and, for serialized
  // product, the SSCC. They come first, and arrive first (the label prints the moment the ship
  // commits; the SSCC job runs ahead of every other PDF).
  const hasPrintFirst = !!shippingLabelButtons || !!ssccButton;

  return (
    <Page className="sales-order-shipping action-page">
      <div className="action-top-bar">
        <div className="order-details">
          <h1>{orderLabel}</h1>
          <h2>{totalQuantity} Products</h2>
        </div>
        <FiXCircle onClick={returnToShipping} />
      </div>
      <div className="action-page-body">
        {isBundled && (
          <nav className="bundle-strip" aria-label={`Shipping bundle ${bundleLabel}`}>
            <div className="strip-eyebrow">
              <RiArchiveStackLine aria-hidden="true" />
              <span className="strip-label">{shipped ? 'Shipped together' : 'Shipping together'}</span>
              <span className="strip-id">{bundleLabel}</span>
            </div>
            <p className="strip-lead">
              One label, one tracking number. Print each order&apos;s own packing list and
              invoice:
            </p>
            <ol className="strip-orders">
              {bundleMembers.map((member) => {
                const isCurrent = member.id === orderId;
                return (
                  <li key={member.id}>
                    <button
                      type="button"
                      className={['strip-order', isCurrent && 'is-current']
                        .filter(Boolean)
                        .join(' ')}
                      aria-current={isCurrent ? 'page' : undefined}
                      disabled={isCurrent}
                      onClick={() =>
                        navigate({
                          pathname: `/sales-order-documents/${member.id}`,
                          search: isShipping ? `?shipping=true&bundle=${shippingBundle.id}` : ''
                        })
                      }
                    >
                      <span className="so-number">{member.vendorSalesOrderNumber}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
        )}
        {showProgress && (
          <ShipProgress
            phase={shipPhase}
            run={shipRun}
            orderLabel={orderLabel}
            expectsCarrierLabel={expectsCarrierLabel}
            labelReady={labelReady}
            documentsReady={documentsReady}
            documentsExpected={expectedDocuments.length}
            onBackToShipping={backToShippingPage}
            checkTimedOut={pendingCheckTimedOut}
            onCheckAgain={onRetryPolling}
          />
        )}
        <div className="print-documents">
          <FiPrinter />
          {/* Mid-ship the progress panel above says it; this line takes over once shipped. */}
          {!shipped ? null : isPolling ? (
            <div className="generating-documents">
              <p>Generating documents...</p>
            </div>
          ) : generationTimedOut ? (
            <div className="generating-documents">
              <p>Some documents are still being generated.</p>
              <Button type="secondary" fullWidth onClick={onRetryPolling}>
                Check Again
              </Button>
            </div>
          ) : (
            <p>
              {rxPrintEnabled
                ? 'Documents are Ready for Print.'
                : 'Documents are Ready to Download.'}
            </p>
          )}
          {hasPrintFirst && (
            <div className="document-group is-priority">
              <h3 className="document-group-title">Print first</h3>
              {/* Render the label buttons whenever labels EXIST — `expectsCarrierLabel` only
                  decides whether to show the waiting placeholder. Keying the buttons off it too
                  would hide real, printable labels from a reprint whenever the order's
                  ShippingMethod row was deleted afterwards (methods are hard-deleted, so
                  `shippingMethod` resolves null). */}
              {shippingLabelButtons}
              {ssccButton}
            </div>
          )}
          <div className="document-group">
            {hasPrintFirst && <h3 className="document-group-title">Documents</h3>}
            <Button
              type="secondary"
              className={docClass(packingListPdf)}
              fullWidth
              disabled={!packingListPdf || sending}
              iconBefore={docIcon(packingListPdf)}
              onClick={() =>
                printDocument('packing-list', packingListDocument, 'packing-list.pdf')
              }
            >
              {actionLabel('packing-list', 'Packing List')}
            </Button>
            <Button
              type="secondary"
              className={docClass(invoicePdf)}
              fullWidth
              disabled={!invoicePdf || sending}
              iconBefore={docIcon(invoicePdf)}
              onClick={() => printDocument('invoice', invoiceDocument, 'invoice.pdf')}
            >
              {actionLabel('invoice', 'Invoice')}
            </Button>
            <Button
              type="secondary"
              className={docClass(stickersZpl)}
              fullWidth
              disabled={!stickersZpl || sending}
              iconBefore={docIcon(stickersZpl)}
              onClick={() =>
                printDocument(
                  'stickers',
                  stickersDocument,
                  stickersDocument?.humanFilename ||
                    `stickers-${vendorSalesOrderNumber || 'order'}.zpl`
                )
              }
            >
              {actionLabel('stickers', 'Stickers')}
            </Button>
            {/* Regenerating is for a shipment that has happened; mid-ship it would race the
                ship's own stickers and certificate. */}
            {shipped && (
              <ReprintStickersButton
                orderId={orderId}
                vendorSalesOrderNumber={vendorSalesOrderNumber}
                vendorStatus={vendorStatus}
                rxPrintEnabled={rxPrintEnabled}
                className="document-print-button"
                onDone={onStickersReprinted}
              />
            )}
            {(expectsCertificateOfOrigin || certificateOfOriginPdf) && (
              <Button
                type="secondary"
                className={docClass(certificateOfOriginPdf)}
                fullWidth
                disabled={!certificateOfOriginPdf || sending}
                iconBefore={docIcon(certificateOfOriginPdf)}
                onClick={() =>
                  printDocument(
                    'certificate-of-origin-shipped',
                    certificateOfOriginDocument,
                    certificateOfOriginDocument?.humanFilename ||
                      `Certificate of Origin - SO ${vendorSalesOrderNumber || 'order'}.pdf`
                  )
                }
              >
                {actionLabel('certificate-of-origin-shipped', 'Certificate of Origin')}
              </Button>
            )}
            {shipped && (
              <CertificateOfOriginButton
                orderId={orderId}
                vendorStatus={vendorStatus}
                shipsToPuertoRico={shipsToPuertoRico}
                rxPrintEnabled={rxPrintEnabled}
                sendingKey={sendingKey}
                onSendToPrinter={onSendToPrinter}
                label="Regenerate Certificate of Origin"
                className="document-print-button"
                onDone={onCertificateOfOriginGenerated}
              />
            )}
            {t3ReportPdf && (
              <Button
                type="secondary"
                className={docClass(t3ReportPdf)}
                fullWidth
                disabled={sending}
                iconBefore={<FiPrinter />}
                onClick={() => printDocument('t3', t3ReportDocument, 't3-report.pdf')}
              >
                {actionLabel('t3', 'T3 Report')}
              </Button>
            )}
            {(hasScheduledItems || t4ReportPdf) && (
              <Button
                type="secondary"
                className={docClass(t4ReportPdf)}
                fullWidth
                disabled={!t4ReportPdf || sending}
                iconBefore={docIcon(t4ReportPdf)}
                onClick={() => printDocument('t4', t4ReportDocument, 't4-report.pdf')}
              >
                {actionLabel('t4', 'T4 Report')}
              </Button>
            )}
          </div>
          {/* The parcels it went out in. A box entered wrong at the bench is corrected on Box Sizes;
              only once the ship has committed, since before that nothing is recorded. */}
          {shipped && shipPackages?.length > 0 && (
            <div className="document-group packages-group">
              <h3 className="document-group-title">
                {shipPackages.length === 1 ? '1 Package' : `${shipPackages.length} Packages`}
              </h3>
              <ul className="packages-group-list">
                {shipPackages.map((pkg) => (
                  <li key={pkg.index}>
                    {pkg.packageType === 'bag' ? <FiShoppingBag aria-hidden="true" /> : <FiBox aria-hidden="true" />}
                    {packageTypeLabel(pkg.packageType)} · {formatSize(pkg.dimensions)}
                  </li>
                ))}
              </ul>
              {shipPackagesEditable && (
                <Button
                  type="secondary"
                  className="document-print-button"
                  fullWidth
                  iconBefore={<FiEdit2 />}
                  onClick={() => navigate(shipPackagesPath(orderId))}
                >
                  Edit Box Sizes
                </Button>
              )}
            </div>
          )}
        </div>

        {isShipping && shipPhase !== 'failed' && (
          // Done closes out a shipment that happened: never before the server has said so.
          <Button
            type="primary"
            fullWidth
            disabled={!shipped}
            iconBefore={shipped ? undefined : <LoadingIcon />}
            onClick={finish}
          >
            {shipped ? 'Done' : 'Shipping…'}
          </Button>
        )}
      </div>
    </Page>
  );
};

export default SalesOrderDocuments;
